import React, { useEffect, useRef, useState } from "react";
import { api, downloadPrivateDocument, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileText, Download, Sparkles, Trash2, ChevronDown, Loader2 } from "lucide-react";
import { applyVisura } from "@/lib/applyVisura";
import { uploadDocument } from "@/lib/uploadDocument";

const LABELS = { company_name: "Ragione sociale", vat_number: "Partita IVA", fiscal_code: "Codice fiscale", address: "Indirizzo", zip: "CAP", city: "Città", province: "Provincia", pec: "PEC" };
const OTHER_TYPES = [{ key: "id_document", label: "Documento d'identità" }, { key: "contract", label: "Contratto" }, { key: "other", label: "Altro" }];
const TYPE_LABELS = { visura: "Visura camerale", id_document: "Documento d'identità", contract: "Contratto", other: "Altro", contratto: "Contratto firmato", kyc_selfie: "Selfie di verifica", kyc_id_document: "Documento di verifica identità" };
const REMOVABLE = ["visura", "id_document", "contract", "other"];
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "");
const unique = (docs) => {
  const seen = new Set();
  return docs.filter((d) => !d.is_deleted).sort((a, b) => String(b.uploaded_at || "").localeCompare(String(a.uploaded_at || ""))).filter((d) => {
    const k = `${d.doc_type}|${d.sha256 || `${d.original_filename}|${d.size}`}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

const DocRow = ({ d, badge, onRemove, children }) => {
  const [ask, setAsk] = useState(false);
  return <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-secondary/40" data-testid={`doc-item-${d.id}`}>
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{d.original_filename || TYPE_LABELS[d.doc_type] || d.doc_type}</p>
        <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5">{TYPE_LABELS[d.doc_type] || d.doc_type}{d.uploaded_at && ` · ${fmtDate(d.uploaded_at)}`}{badge && <Badge variant="outline" className="text-[10px]">{badge}</Badge>}</p>
      </div>
    </div>
    <div className="compact-actions flex items-center gap-1.5 shrink-0">
      {children}
      {d.storage_path && <Button size="sm" variant="ghost" onClick={() => downloadPrivateDocument(d).catch(() => toast.error("Documento non disponibile. Riprova."))} aria-label={`Scarica ${d.original_filename}`} data-testid={`doc-view-${d.id}`}><Download className="w-4 h-4" /></Button>}
      {onRemove && <Button size="sm" variant={ask ? "destructive" : "ghost"} onClick={() => (ask ? onRemove(d) : setAsk(true))} onBlur={() => setAsk(false)} aria-label="Rimuovi documento" data-testid={`doc-remove-${d.id}`}><Trash2 className="w-4 h-4" />{ask && "Conferma"}</Button>}
    </div>
  </div>;
};

const Differ = ({ differ, onApply, onIgnore }) => <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2" data-testid="visura-differ">
  <p className="text-sm font-medium">La visura riporta dati diversi da quelli salvati:</p>
  <table className="text-sm"><tbody>{Object.entries(differ).map(([k, v]) => <tr key={k}><td className="pr-3 text-muted-foreground">{LABELS[k]}</td><td className="font-medium">{v}</td></tr>)}</tbody></table>
  <div className="compact-actions flex gap-2"><Button size="sm" onClick={onApply} data-testid="visura-differ-confirm">Usa i dati della visura</Button><Button size="sm" variant="ghost" onClick={onIgnore} data-testid="visura-differ-ignore">Mantieni i miei</Button></div>
</div>;

const Progress = ({ p }) => p && <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="doc-upload-progress"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {p.phase} {p.percent ? `${p.percent}%` : ""}</p>;

const VisuraCard = ({ visure, reading, busy, progress, onSync, onUpload, onRemove, differ, onApply, onIgnore }) => {
  const [showOld, setShowOld] = useState(false);
  const ref = useRef();
  const [latest, ...old] = visure;
  return <Card className="p-5 sm:p-6 glow-border space-y-4" data-testid="visura-card">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div><h2 className="font-head text-lg font-semibold">Visura camerale</h2><p className="text-sm text-muted-foreground">Usiamo la visura più recente per completare la tua anagrafica. Poi la verifica l'amministratore.</p></div>
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f, "visura"); }} data-testid="visura-file-input" />
      <Button variant="outline" className="gap-2" disabled={!!busy || reading} onClick={() => ref.current?.click()} data-testid="visura-upload-button"><Upload className="w-4 h-4" />{latest ? "Carica nuova visura" : "Carica visura"}</Button>
    </div>
    {busy === "visura" && <Progress p={progress} />}
    {latest ? <DocRow d={latest} badge="In uso">
      <Button size="sm" className="gap-1.5" disabled={reading} onClick={() => onSync(latest)} data-testid="visura-sync-button">{reading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}{reading ? "Lettura…" : "Aggiorna anagrafica"}</Button>
    </DocRow> : <p className="text-sm text-muted-foreground" data-testid="visura-empty">Nessuna visura caricata.</p>}
    {reading && <p className="text-sm text-primary flex items-center gap-2" data-testid="visura-reading"><Sparkles className="w-4 h-4 animate-pulse" /> Sto leggendo la visura: può servire fino a un minuto. Intanto puoi usare la pagina.</p>}
    {differ && <Differ differ={differ} onApply={onApply} onIgnore={onIgnore} />}
    {old.length > 0 && <div className="space-y-2">
      <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground" onClick={() => setShowOld(!showOld)} data-testid="visura-old-toggle"><ChevronDown className={`w-4 h-4 transition-transform ${showOld ? "rotate-180" : ""}`} />Versioni precedenti ({old.length})</Button>
      {showOld && <div className="space-y-2" data-testid="visura-old-list">{old.map((d) => <DocRow key={d.id} d={d} onRemove={onRemove} />)}</div>}
    </div>}
  </Card>;
};

const OtherDocsCard = ({ docs, busy, progress, onUpload, onRemove }) => {
  const [type, setType] = useState("id_document");
  const ref = useRef();
  return <Card className="p-5 sm:p-6 glow-border space-y-4" data-testid="other-docs-card">
    <div><h2 className="font-head text-lg font-semibold">Altri documenti</h2><p className="text-sm text-muted-foreground">Documento d'identità, contratti e altri file. Formati: PDF, PNG, JPG, WEBP.</p></div>
    <div className="flex flex-col sm:flex-row gap-2">
      <Select value={type} onValueChange={setType}><SelectTrigger className="sm:w-60" data-testid="doc-type-select"><SelectValue /></SelectTrigger><SelectContent>{OTHER_TYPES.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}</SelectContent></Select>
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f, type); }} data-testid="doc-file-input" />
      <Button className="gap-2" disabled={!!busy} onClick={() => ref.current?.click()} data-testid="upload-trigger-button"><Upload className="w-4 h-4" />Carica documento</Button>
    </div>
    {busy === "other" && <Progress p={progress} />}
    <div className="space-y-2">
      {docs.map((d) => <DocRow key={d.id} d={d} onRemove={REMOVABLE.includes(d.doc_type) ? onRemove : null} />)}
      {docs.length === 0 && <p className="text-sm text-muted-foreground" data-testid="other-docs-empty">Nessun altro documento.</p>}
    </div>
  </Card>;
};

export default function Documents() {
  const { refresh } = useAuth();
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const [progress, setProgress] = useState(null);
  const [reading, setReading] = useState(false), [differ, setDiffer] = useState(null);
  const load = () => api.get("/auth/me").then((r) => setDocs(r.data.documents || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const list = unique(docs);
  const visure = list.filter((d) => d.doc_type === "visura");

  const syncVisura = async (blob, name) => {
    setReading(true);
    try {
      const r = await applyVisura(blob, name);
      refresh();
      toast.success(r.filled.length ? `Anagrafica aggiornata: ${r.filled.map((k) => LABELS[k]).join(", ")}` : "Visura letta: l'anagrafica era già completa");
      setDiffer(Object.keys(r.differ).length ? r.differ : null);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Lettura della visura non riuscita: puoi inserire i dati a mano"); }
    finally { setReading(false); }
  };
  const fromSaved = async (d) => {
    setReading(true);
    try {
      const { data } = await api.get(`/files/${d.storage_path.split("/").map(encodeURIComponent).join("/")}`, { responseType: "blob" });
      await syncVisura(data, d.original_filename || "visura.pdf");
    } catch { setReading(false); toast.error("Documento non disponibile. Riprova."); }
  };
  const upload = async (file, type) => {
    setBusy(type === "visura" ? "visura" : "other");
    try {
      const saved = await uploadDocument(file, type, setProgress);
      toast.success(docs.some((d) => d.id === saved.id && !d.is_deleted) ? "Questo file era già caricato: non l'ho duplicato" : "Documento caricato");
      await load();
      refresh();
      if (type === "visura") syncVisura(file, file.name);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore caricamento"); }
    finally { setBusy(""); setProgress(null); }
  };
  const remove = async (d) => {
    try { await api.delete(`/account/documents/${d.id}`); toast.success("Documento rimosso"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Rimozione non riuscita"); }
  };
  const applyDiffer = async () => {
    try { await api.put("/account/profile", differ); refresh(); toast.success("Dati sostituiti con quelli della visura"); setDiffer(null); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Aggiornamento non riuscito"); }
  };

  return <div className="space-y-6 max-w-3xl" data-testid="documents-page">
    <div>
      <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Onboarding dealer</p>
      <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Documenti & Visura</h1>
    </div>
    <VisuraCard visure={visure} reading={reading} busy={busy} progress={progress} onSync={fromSaved} onUpload={upload} onRemove={remove} differ={differ} onApply={applyDiffer} onIgnore={() => setDiffer(null)} />
    <OtherDocsCard docs={list.filter((d) => d.doc_type !== "visura")} busy={busy} progress={progress} onUpload={upload} onRemove={remove} />
  </div>;
}
